import { useEffect } from 'react';

/**
 * AnalyticsLoader
 *
 * Loads third-party analytics scripts ONLY after the user has explicitly
 * accepted non-essential cookies. Respects GDPR / ePrivacy by:
 *   - blocking script injection until consent === 'accepted'
 *   - removing injected scripts + clearing tracking cookies on revoke
 *   - reacting live to consent changes (cross-tab + same-tab)
 *
 * Currently wires up Google Analytics 4 via `REACT_APP_GA_MEASUREMENT_ID`.
 * Additional providers can be added inside `loadProviders()` following the
 * same pattern (feature-flag by env var, mark injected nodes with
 * `data-analytics="<provider>"` so `unload()` can remove them).
 */

const STORAGE_KEY = 'heliophant-cookie-consent';
// Reads a NON-SENSITIVE preference ('accepted' | 'rejected'). No PII / no tokens.
const ANALYTICS_ATTR = 'data-analytics';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID; // e.g. G-XXXXXXX

function isAccepted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'accepted';
  } catch {
    return false;
  }
}

function loadGA() {
  if (!GA_ID) return;
  if (document.querySelector(`script[${ANALYTICS_ATTR}="ga"]`)) return;

  const s1 = document.createElement('script');
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  s1.setAttribute(ANALYTICS_ATTR, 'ga');
  document.head.appendChild(s1);

  const s2 = document.createElement('script');
  s2.setAttribute(ANALYTICS_ATTR, 'ga');
  s2.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('consent', 'default', { 'analytics_storage': 'granted' });
    gtag('config', '${GA_ID}', { anonymize_ip: true });
  `;
  document.head.appendChild(s2);
}

function unloadAll() {
  document.querySelectorAll(`script[${ANALYTICS_ATTR}]`).forEach((n) => n.remove());
  // Clear GA cookies (best-effort; must match host + path).
  const host = window.location.hostname;
  const cookiePrefixes = ['_ga', '_gid', '_gat'];
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (cookiePrefixes.some((p) => name.startsWith(p))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    }
  });
  // Reset gtag state
  try {
    if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'denied' });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('gtag consent update failed:', e);
    }
  }
}

function apply() {
  if (isAccepted()) loadGA();
  else unloadAll();
}

const AnalyticsLoader = () => {
  useEffect(() => {
    apply();
    const onChange = () => apply();
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) apply();
    };
    window.addEventListener('heliophant:cookie-consent-changed', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('heliophant:cookie-consent-changed', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return null;
};

export default AnalyticsLoader;
